import type {
  DirectorCompileResult,
  NarrativePreviewModel,
  OmniStudioGraph,
} from "@/lib/omnistudio/compiler/directorCompiler.types";
import type {
  WorldNarrativeCompileProposal,
} from "@/lib/world/types/worldProposal.types";
import type {
  WorldProposalStagedVariantRecord,
  WorldProposalReviewTraceFocus,
} from "@/lib/world/review/worldProposalReview.types";

export const OMNISTUDIO_COMPILE_REQUEST_EVENT = "omnistudio:compile:request";
export const OMNISTUDIO_COMPILE_RESULT_EVENT = "omnistudio:compile:result";
export const OMNISTUDIO_COMPILE_APPLY_EVENT = "omnistudio:compile:apply";
export const OMNISTUDIO_COMPILE_APPLIED_EVENT = "omnistudio:compile:applied";
export const OMNISTUDIO_COMPILE_PREVIEW_SET_EVENT = "omnistudio:compile:preview:set";
export const OMNISTUDIO_COMPILE_PREVIEW_CLEAR_EVENT = "omnistudio:compile:preview:clear";
export const OMNISTUDIO_INGEST_CANDIDATES_SET_EVENT = "omnistudio:ingest:candidates:set";
export const OMNISTUDIO_INGEST_CANDIDATES_PATCH_EVENT = "omnistudio:ingest:candidates:patch";
export const OMNISTUDIO_INGEST_CANDIDATES_CLEAR_EVENT = "omnistudio:ingest:candidates:clear";
export const OMNISTUDIO_INGEST_FOCUS_SET_EVENT = "omnistudio:ingest:focus:set";
export const OMNISTUDIO_WORLD_PROPOSAL_REVIEW_EVENT = "omnistudio:worldProposal:review";
export const OMNICINE_NARRATIVE_PREVIEW_SET_EVENT = "omnicine:narrative:preview:set";
export const OMNICINE_NARRATIVE_PREVIEW_CLEAR_EVENT = "omnicine:narrative:preview:clear";
export const OMNISTUDIO_SCENE_SELECT_EVENT = "omnistudio:scene:select";
export const OMNICINE_CLIP_SELECTED_EVENT = "omnicine:clip:selected";
export const OMNICINE_INLINE_WITNESS_OPEN_GLASS_EVENT = "omnicine:inlineWitness:openGlass";
export const OMNICINE_INLINE_WITNESS_REMOVE_CLIP_EVENT = "omnicine:inlineWitness:removeClip";
export const OMNICINE_CLIP_URL_RESOLVED_EVENT = "omnicine:clip:url:resolved";
export const OMNICINE_ANNOTATION_DRAFT_HANDOFF_EVENT = "omnicine:annotation:draft:handoff";

const OMNICINE_ANNOTATION_DRAFT_HANDOFF_STORAGE_KEY = "omnicine.annotationDraftHandoff.v1";
const OMNISTUDIO_WORLD_PROPOSAL_REVIEW_STORAGE_KEY = "omnistudio.worldProposalReview.v1";

export type OmniStudioBridgeSource =
  | "omnistudio-shell"
  | "omnistudio-compile-bridge"
  | "omnistudio-apply"
  | "timeline-preview"
  | "world-proposal-generator";

export type OmniStudioCompileRequestDetail = {
  graph: OmniStudioGraph;
  requestedAt: number;
  source: "omnistudio-shell";
};

export type OmniStudioCompileResultDetail = {
  result: DirectorCompileResult;
  source: "omnistudio-compile-bridge";
};

export type OmniStudioCompileApplyRequestDetail = {
  result: DirectorCompileResult;
  requestedAt: number;
  source: "omnistudio-shell";
};

export type OmniStudioCompileAppliedDetail = {
  compileId: string;
  appliedAt: number;
  source: "omnistudio-apply";
};

export type OmniStudioPreviewDetail = {
  preview: NarrativePreviewModel;
  source: "omnistudio-compile-bridge";
};

export type OmniStudioWorldProposalReviewDetail = {
  proposal: WorldNarrativeCompileProposal;
  source: Extract<OmniStudioBridgeSource, "world-proposal-generator" | "omnistudio-shell">;
  traceFocus?: WorldProposalReviewTraceFocus | null;
  stagedVariant?: WorldProposalStagedVariantRecord;
  receivedAt: number;
};

export type OmniStudioIngestCandidateKind =
  | "video"
  | "audio"
  | "image"
  | "npc"
  | "scene"
  | "arc"
  | "note"
  | "unknown";

export type OmniStudioIngestCandidateStatus =
  | "queued"
  | "resolving"
  | "ready"
  | "blocked"
  | "applied";

export type OmniStudioIngestArrangementMode = "stack" | "grid" | "timeline" | "priority";

export type OmniStudioIngestArrangementHints = {
  laneHint?: string;
  priority?: number;
  order?: number;
  mode?: OmniStudioIngestArrangementMode;
  groupLabel?: string;
};

export type OmniStudioIngestCandidate = {
  id: string;
  kind: OmniStudioIngestCandidateKind;
  title: string;
  subtitle?: string;
  mediaUrl?: string;
  audioUrl?: string;
  posterUrl?: string;
  mediaId?: string;
  sceneId?: string;
  arcId?: string;
  sourceLabel?: string;
  previewTip?: string;
  status?: OmniStudioIngestCandidateStatus;
  processingMs?: number;
  durationSec?: number;
  confidence?: number;
  previewOnly?: boolean;
  groupKey?: string;
  arrangementHints?: OmniStudioIngestArrangementHints;
  updatedAt?: number;
  metadata?: Record<string, unknown>;
};

export type OmniStudioIngestCandidatesSetDetail = {
  candidates: OmniStudioIngestCandidate[];
  source: OmniStudioBridgeSource;
  setAt: number;
};

export type OmniStudioIngestCandidatesPatchDetail = {
  candidates: OmniStudioIngestCandidate[];
  source: OmniStudioBridgeSource;
  patchedAt: number;
};

export type OmniStudioIngestCandidatesClearDetail = {
  source: OmniStudioBridgeSource;
  clearedAt: number;
};

export type OmniStudioIngestFocusSetDetail = {
  candidateId?: string;
  source: OmniStudioBridgeSource;
  focusedAt: number;
};

export type OmniStudioPreviewClearDetail = {
  source: OmniStudioBridgeSource;
  clearedAt: number;
};

export type OmniStudioSceneSelectDetail = {
  sceneId: string;
  source: OmniStudioBridgeSource;
};

export type OmniCineClipSelectedDetail = {
  clipId: string;
  source?: string;
};

export type OmniCineInlineWitnessOpenGlassDetail = {
  clipId: string;
  kind?: string;
  clipLabel?: string;
  hasLinkedAudio?: boolean;
  playheadSec?: number;
  source?: "inlineWitness" | "system" | "mediaPanel" | "import";
  open?: boolean;
  mode?: string;
  pinned?: boolean;
  force?: boolean;
  preserveWitnessBar?: boolean;
};

export type OmniCineInlineWitnessRemoveClipDetail = {
  clipId: string;
  source?: string;
};

export type OmniCineClipUrlResolvedDetail = {
  clipId?: string;
  mediaId?: string;
  url?: string;
  kind?: string;
  source?: string;
  error?: string;
};

export type OmniCineAnnotationDraftHandoffDetail = {
  draftNote: string;
  draftNextAction?: string | null;
  suggestedKind?: string | null;
  sourceSurface: "omnistudio";
  sourceLabel?: string | null;
  sourceNoteId?: string | null;
  sourceSceneId?: string | null;
  sourceSceneTitle?: string | null;
  openSurface?: boolean;
  queuedAt: number;
};

type WindowEventHandler<TDetail> = (detail: TDetail, event: CustomEvent<TDetail>) => void;

function emitWindowEvent<TDetail>(eventName: string, detail: TDetail): void {
  window.dispatchEvent(new CustomEvent<TDetail>(eventName, { detail }));
}

function readString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function normalizeAnnotationDraftHandoffDetail(
  detail: Partial<OmniCineAnnotationDraftHandoffDetail> | null | undefined,
): OmniCineAnnotationDraftHandoffDetail | null {
  const draftNote = readString(detail?.draftNote);
  if (!draftNote) return null;

  return {
    draftNote,
    draftNextAction: readString(detail?.draftNextAction) ?? null,
    suggestedKind: readString(detail?.suggestedKind) ?? null,
    sourceSurface: "omnistudio",
    sourceLabel: readString(detail?.sourceLabel) ?? null,
    sourceNoteId: readString(detail?.sourceNoteId) ?? null,
    sourceSceneId: readString(detail?.sourceSceneId) ?? null,
    sourceSceneTitle: readString(detail?.sourceSceneTitle) ?? null,
    openSurface: detail?.openSurface !== false,
    queuedAt: typeof detail?.queuedAt === "number" && Number.isFinite(detail.queuedAt) ? detail.queuedAt : Date.now(),
  };
}

function writePendingAnnotationDraftHandoff(detail: OmniCineAnnotationDraftHandoffDetail): void {
  try {
    window.localStorage.setItem(OMNICINE_ANNOTATION_DRAFT_HANDOFF_STORAGE_KEY, JSON.stringify(detail));
  } catch {
    // Ignore storage failures and rely on the immediate window event.
  }
}

function normalizeWorldProposalReviewDetail(
  detail: Partial<OmniStudioWorldProposalReviewDetail> | null | undefined,
): OmniStudioWorldProposalReviewDetail | null {
  if (!detail?.proposal) return null;
  if (!readString(detail.proposal.proposalId) || !readString(detail.proposal.worldId)) return null;
  if (!Array.isArray(detail.proposal.sceneSeeds) || !Array.isArray(detail.proposal.beatSeeds) || !Array.isArray(detail.proposal.arcSeeds)) {
    return null;
  }

  return {
    proposal: detail.proposal,
    source: detail.source === "omnistudio-shell" ? "omnistudio-shell" : "world-proposal-generator",
    traceFocus: normalizeWorldProposalTraceFocus(detail.traceFocus),
    stagedVariant: normalizeWorldProposalStagedVariant(detail.stagedVariant),
    receivedAt: typeof detail.receivedAt === "number" && Number.isFinite(detail.receivedAt) ? detail.receivedAt : Date.now(),
  };
}

function normalizeWorldProposalStagedVariant(
  stagedVariant: Partial<WorldProposalStagedVariantRecord> | null | undefined,
): WorldProposalStagedVariantRecord | undefined {
  if (!stagedVariant) return undefined;

  const variantId = readString(stagedVariant.variantId);
  const baseProposalId = readString(stagedVariant.baseProposalId);
  const label = readString(stagedVariant.label);
  const summary = readString(stagedVariant.summary);
  const refinementMode = readString(stagedVariant.refinementMode);
  const proposalType = stagedVariant.proposalType;
  const emotionAxis = readString(stagedVariant.emotionAxis);

  if (!variantId || !baseProposalId || !label || !summary || !refinementMode || !emotionAxis) {
    return undefined;
  }

  if (proposalType !== "scene" && proposalType !== "beat" && proposalType !== "arc") {
    return undefined;
  }

  return {
    variantId,
    baseProposalId,
    proposalType,
    label,
    summary,
    refinementMode,
    derivedFromBias: readString(stagedVariant.derivedFromBias) ?? undefined,
    intensity: typeof stagedVariant.intensity === "number" && Number.isFinite(stagedVariant.intensity)
      ? stagedVariant.intensity
      : 3,
    emotionAxis,
  };
}

function normalizeWorldProposalTraceFocus(
  traceFocus: Partial<WorldProposalReviewTraceFocus> | null | undefined,
): WorldProposalReviewTraceFocus | null {
  if (!traceFocus) return null;

  const proposalId = readString(traceFocus.proposalId);
  const label = readString(traceFocus.label);
  const summary = readString(traceFocus.summary);
  const kind = traceFocus.kind;

  if (!proposalId || !label || !summary || (kind !== "scene" && kind !== "beat" && kind !== "arc")) {
    return null;
  }

  return {
    proposalId,
    kind,
    label,
    summary,
    zoneIds: Array.isArray(traceFocus.zoneIds) ? traceFocus.zoneIds.filter((value): value is string => Boolean(readString(value))) : [],
    sceneIds: Array.isArray(traceFocus.sceneIds) ? traceFocus.sceneIds.filter((value): value is string => Boolean(readString(value))) : [],
    emotionalIntensity:
      typeof traceFocus.emotionalIntensity === "number" && Number.isFinite(traceFocus.emotionalIntensity)
        ? Math.max(0, Math.min(1, traceFocus.emotionalIntensity))
        : 0.5,
  };
}

function readPendingAnnotationDraftHandoff(): OmniCineAnnotationDraftHandoffDetail | null {
  try {
    const raw = window.localStorage.getItem(OMNICINE_ANNOTATION_DRAFT_HANDOFF_STORAGE_KEY);
    if (!raw) return null;

    return normalizeAnnotationDraftHandoffDetail(JSON.parse(raw) as Partial<OmniCineAnnotationDraftHandoffDetail>);
  } catch {
    return null;
  }
}

function writePendingWorldProposalReview(detail: OmniStudioWorldProposalReviewDetail): void {
  try {
    window.localStorage.setItem(OMNISTUDIO_WORLD_PROPOSAL_REVIEW_STORAGE_KEY, JSON.stringify(detail));
  } catch {
    // Ignore storage failures and rely on the immediate window event.
  }
}

function readPendingWorldProposalReview(): OmniStudioWorldProposalReviewDetail | null {
  try {
    const raw = window.localStorage.getItem(OMNISTUDIO_WORLD_PROPOSAL_REVIEW_STORAGE_KEY);
    if (!raw) return null;

    return normalizeWorldProposalReviewDetail(JSON.parse(raw) as Partial<OmniStudioWorldProposalReviewDetail>);
  } catch {
    return null;
  }
}

function subscribeWindowEvent<TDetail>(eventName: string, handler: WindowEventHandler<TDetail>): () => void {
  const listener: EventListener = (event) => {
    const customEvent = event as CustomEvent<TDetail>;
    handler(customEvent.detail, customEvent);
  };

  window.addEventListener(eventName, listener);
  return () => window.removeEventListener(eventName, listener);
}

export function requestOmniStudioCompile(graph: OmniStudioGraph): void {
  emitWindowEvent<OmniStudioCompileRequestDetail>(OMNISTUDIO_COMPILE_REQUEST_EVENT, {
    graph,
    requestedAt: Date.now(),
    source: "omnistudio-shell",
  });
}

export function onOmniStudioCompileRequest(handler: WindowEventHandler<OmniStudioCompileRequestDetail>): () => void {
  return subscribeWindowEvent(OMNISTUDIO_COMPILE_REQUEST_EVENT, handler);
}

export function emitOmniStudioCompileResult(result: DirectorCompileResult): void {
  emitWindowEvent<OmniStudioCompileResultDetail>(OMNISTUDIO_COMPILE_RESULT_EVENT, {
    result,
    source: "omnistudio-compile-bridge",
  });
}

export function onOmniStudioCompileResult(handler: WindowEventHandler<OmniStudioCompileResultDetail>): () => void {
  return subscribeWindowEvent(OMNISTUDIO_COMPILE_RESULT_EVENT, handler);
}

export function requestOmniStudioApply(result: DirectorCompileResult): void {
  emitWindowEvent<OmniStudioCompileApplyRequestDetail>(OMNISTUDIO_COMPILE_APPLY_EVENT, {
    result,
    requestedAt: Date.now(),
    source: "omnistudio-shell",
  });
}

export function onOmniStudioApplyRequest(handler: WindowEventHandler<OmniStudioCompileApplyRequestDetail>): () => void {
  return subscribeWindowEvent(OMNISTUDIO_COMPILE_APPLY_EVENT, handler);
}

export function emitOmniStudioCompileApplied(compileId: string): void {
  emitWindowEvent<OmniStudioCompileAppliedDetail>(OMNISTUDIO_COMPILE_APPLIED_EVENT, {
    compileId,
    appliedAt: Date.now(),
    source: "omnistudio-apply",
  });
}

export function onOmniStudioCompileApplied(handler: WindowEventHandler<OmniStudioCompileAppliedDetail>): () => void {
  return subscribeWindowEvent(OMNISTUDIO_COMPILE_APPLIED_EVENT, handler);
}

export function emitOmniStudioCompilePreviewSet(preview: NarrativePreviewModel): void {
  emitWindowEvent<OmniStudioPreviewDetail>(OMNISTUDIO_COMPILE_PREVIEW_SET_EVENT, {
    preview,
    source: "omnistudio-compile-bridge",
  });
}

export function onOmniStudioCompilePreviewSet(handler: WindowEventHandler<OmniStudioPreviewDetail>): () => void {
  return subscribeWindowEvent(OMNISTUDIO_COMPILE_PREVIEW_SET_EVENT, handler);
}

export function emitOmniStudioCompilePreviewClear(source: OmniStudioBridgeSource): void {
  emitWindowEvent<OmniStudioPreviewClearDetail>(OMNISTUDIO_COMPILE_PREVIEW_CLEAR_EVENT, {
    source,
    clearedAt: Date.now(),
  });
}

export function onOmniStudioCompilePreviewClear(handler: WindowEventHandler<OmniStudioPreviewClearDetail>): () => void {
  return subscribeWindowEvent(OMNISTUDIO_COMPILE_PREVIEW_CLEAR_EVENT, handler);
}

export function emitOmniStudioIngestCandidatesSet(
  candidates: OmniStudioIngestCandidate[],
  source: OmniStudioBridgeSource,
): void {
  emitWindowEvent<OmniStudioIngestCandidatesSetDetail>(OMNISTUDIO_INGEST_CANDIDATES_SET_EVENT, {
    candidates,
    source,
    setAt: Date.now(),
  });
}

export function onOmniStudioIngestCandidatesSet(
  handler: WindowEventHandler<OmniStudioIngestCandidatesSetDetail>,
): () => void {
  return subscribeWindowEvent(OMNISTUDIO_INGEST_CANDIDATES_SET_EVENT, handler);
}

export function emitOmniStudioIngestCandidatesPatch(
  candidates: OmniStudioIngestCandidate[],
  source: OmniStudioBridgeSource,
): void {
  emitWindowEvent<OmniStudioIngestCandidatesPatchDetail>(OMNISTUDIO_INGEST_CANDIDATES_PATCH_EVENT, {
    candidates,
    source,
    patchedAt: Date.now(),
  });
}

export function onOmniStudioIngestCandidatesPatch(
  handler: WindowEventHandler<OmniStudioIngestCandidatesPatchDetail>,
): () => void {
  return subscribeWindowEvent(OMNISTUDIO_INGEST_CANDIDATES_PATCH_EVENT, handler);
}

export function emitOmniStudioIngestCandidatesClear(source: OmniStudioBridgeSource): void {
  emitWindowEvent<OmniStudioIngestCandidatesClearDetail>(OMNISTUDIO_INGEST_CANDIDATES_CLEAR_EVENT, {
    source,
    clearedAt: Date.now(),
  });
}

export function onOmniStudioIngestCandidatesClear(
  handler: WindowEventHandler<OmniStudioIngestCandidatesClearDetail>,
): () => void {
  return subscribeWindowEvent(OMNISTUDIO_INGEST_CANDIDATES_CLEAR_EVENT, handler);
}

export function emitOmniStudioIngestFocusSet(candidateId: string | undefined, source: OmniStudioBridgeSource): void {
  emitWindowEvent<OmniStudioIngestFocusSetDetail>(OMNISTUDIO_INGEST_FOCUS_SET_EVENT, {
    candidateId,
    source,
    focusedAt: Date.now(),
  });
}

export function onOmniStudioIngestFocusSet(
  handler: WindowEventHandler<OmniStudioIngestFocusSetDetail>,
): () => void {
  return subscribeWindowEvent(OMNISTUDIO_INGEST_FOCUS_SET_EVENT, handler);
}

export function emitOmniStudioWorldProposalReview(
  proposal: WorldNarrativeCompileProposal,
  source: OmniStudioWorldProposalReviewDetail["source"] = "world-proposal-generator",
  traceFocus?: WorldProposalReviewTraceFocus | null,
  stagedVariant?: WorldProposalStagedVariantRecord,
): void {
  const detail: OmniStudioWorldProposalReviewDetail = {
    proposal,
    source,
    traceFocus: traceFocus ?? null,
    stagedVariant,
    receivedAt: Date.now(),
  };

  writePendingWorldProposalReview(detail);
  emitWindowEvent<OmniStudioWorldProposalReviewDetail>(OMNISTUDIO_WORLD_PROPOSAL_REVIEW_EVENT, detail);
}

export function stageOmniStudioWorldProposalReviewHandoff(
  proposal: WorldNarrativeCompileProposal,
  source: OmniStudioWorldProposalReviewDetail["source"] = "world-proposal-generator",
  traceFocus?: WorldProposalReviewTraceFocus | null,
  stagedVariant?: WorldProposalStagedVariantRecord,
): OmniStudioWorldProposalReviewDetail {
  const detail: OmniStudioWorldProposalReviewDetail = {
    proposal,
    source,
    traceFocus: traceFocus ?? null,
    stagedVariant,
    receivedAt: Date.now(),
  };

  writePendingWorldProposalReview(detail);
  return detail;
}

export function onOmniStudioWorldProposalReview(
  handler: WindowEventHandler<OmniStudioWorldProposalReviewDetail>,
): () => void {
  return subscribeWindowEvent(OMNISTUDIO_WORLD_PROPOSAL_REVIEW_EVENT, handler);
}

export function consumePendingOmniStudioWorldProposalReview(): OmniStudioWorldProposalReviewDetail | null {
  const pending = readPendingWorldProposalReview();

  try {
    window.localStorage.removeItem(OMNISTUDIO_WORLD_PROPOSAL_REVIEW_STORAGE_KEY);
  } catch {
    // Ignore storage failures.
  }

  return pending;
}

export function emitOmniCineNarrativePreviewSet(preview: NarrativePreviewModel): void {
  emitWindowEvent<OmniStudioPreviewDetail>(OMNICINE_NARRATIVE_PREVIEW_SET_EVENT, {
    preview,
    source: "omnistudio-compile-bridge",
  });
}

export function onOmniCineNarrativePreviewSet(handler: WindowEventHandler<OmniStudioPreviewDetail>): () => void {
  return subscribeWindowEvent(OMNICINE_NARRATIVE_PREVIEW_SET_EVENT, handler);
}

export function emitOmniCineNarrativePreviewClear(source: OmniStudioBridgeSource): void {
  emitWindowEvent<OmniStudioPreviewClearDetail>(OMNICINE_NARRATIVE_PREVIEW_CLEAR_EVENT, {
    source,
    clearedAt: Date.now(),
  });
}

export function onOmniCineNarrativePreviewClear(handler: WindowEventHandler<OmniStudioPreviewClearDetail>): () => void {
  return subscribeWindowEvent(OMNICINE_NARRATIVE_PREVIEW_CLEAR_EVENT, handler);
}

export function emitOmniStudioSceneSelect(sceneId: string, source: OmniStudioBridgeSource): void {
  emitWindowEvent<OmniStudioSceneSelectDetail>(OMNISTUDIO_SCENE_SELECT_EVENT, { sceneId, source });
}

export function onOmniStudioSceneSelect(handler: WindowEventHandler<OmniStudioSceneSelectDetail>): () => void {
  return subscribeWindowEvent(OMNISTUDIO_SCENE_SELECT_EVENT, handler);
}

export function emitOmniCineClipSelected(clipId: string, source?: string): void {
  emitWindowEvent<OmniCineClipSelectedDetail>(OMNICINE_CLIP_SELECTED_EVENT, { clipId, source });
}

export function onOmniCineClipSelected(handler: WindowEventHandler<OmniCineClipSelectedDetail>): () => void {
  return subscribeWindowEvent(OMNICINE_CLIP_SELECTED_EVENT, handler);
}

export function emitOmniCineInlineWitnessOpenGlass(detail: OmniCineInlineWitnessOpenGlassDetail): void {
  emitWindowEvent<OmniCineInlineWitnessOpenGlassDetail>(OMNICINE_INLINE_WITNESS_OPEN_GLASS_EVENT, detail);
}

export function onOmniCineInlineWitnessOpenGlass(
  handler: WindowEventHandler<OmniCineInlineWitnessOpenGlassDetail>,
): () => void {
  return subscribeWindowEvent(OMNICINE_INLINE_WITNESS_OPEN_GLASS_EVENT, handler);
}

export function emitOmniCineInlineWitnessRemoveClip(detail: OmniCineInlineWitnessRemoveClipDetail): void {
  emitWindowEvent<OmniCineInlineWitnessRemoveClipDetail>(OMNICINE_INLINE_WITNESS_REMOVE_CLIP_EVENT, detail);
}

export function onOmniCineInlineWitnessRemoveClip(
  handler: WindowEventHandler<OmniCineInlineWitnessRemoveClipDetail>,
): () => void {
  return subscribeWindowEvent(OMNICINE_INLINE_WITNESS_REMOVE_CLIP_EVENT, handler);
}

export function emitOmniCineClipUrlResolved(detail: OmniCineClipUrlResolvedDetail): void {
  emitWindowEvent<OmniCineClipUrlResolvedDetail>(OMNICINE_CLIP_URL_RESOLVED_EVENT, detail);
}

export function onOmniCineClipUrlResolved(handler: WindowEventHandler<OmniCineClipUrlResolvedDetail>): () => void {
  return subscribeWindowEvent(OMNICINE_CLIP_URL_RESOLVED_EVENT, handler);
}

export function stageOmniCineAnnotationDraftHandoff(
  detail: Omit<OmniCineAnnotationDraftHandoffDetail, "queuedAt" | "sourceSurface"> & { sourceSurface?: "omnistudio" },
): OmniCineAnnotationDraftHandoffDetail | null {
  const normalized = normalizeAnnotationDraftHandoffDetail({
    ...detail,
    sourceSurface: "omnistudio",
    queuedAt: Date.now(),
  });
  if (!normalized) return null;

  writePendingAnnotationDraftHandoff(normalized);
  emitWindowEvent<OmniCineAnnotationDraftHandoffDetail>(OMNICINE_ANNOTATION_DRAFT_HANDOFF_EVENT, normalized);
  return normalized;
}

export function onOmniCineAnnotationDraftHandoff(
  handler: WindowEventHandler<OmniCineAnnotationDraftHandoffDetail>,
): () => void {
  return subscribeWindowEvent(OMNICINE_ANNOTATION_DRAFT_HANDOFF_EVENT, handler);
}

export function consumePendingOmniCineAnnotationDraftHandoff(): OmniCineAnnotationDraftHandoffDetail | null {
  const pending = readPendingAnnotationDraftHandoff();

  try {
    window.localStorage.removeItem(OMNICINE_ANNOTATION_DRAFT_HANDOFF_STORAGE_KEY);
  } catch {
    // Ignore storage failures.
  }

  return pending;
}